import React from "react";
import {
  Popover,
  Box,
  Typography,
  IconButton,
  MenuItem,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CartItem, removeItem, setQuantity } from "../../store/cartSlice";
import { useDispatch } from "react-redux";
import BuyButton from "../single_item_page/BuyButton";
import CustomSelect from "./CustomSelect";
import { MAX_QUANITY_PER_PRODUCT } from "../conts";

type CartPopoverProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: CartItem[];
};

const StyledPopover = styled(Popover)({
  "& .MuiPaper-root": {
    width: "clamp(260px, 80vw, 400px)",
    padding: 16,
    borderRadius: 8,
    boxShadow: "0px 4px 18px rgba(0,0,0,0.15)",
  },
});

const EmptyText = styled(Typography)({
  textAlign: "center",
  padding: "32px 0",
  color: "gray",
});

const ItemsContainer = styled(Box)({
  maxHeight: 240,
  overflowY: "auto",
  paddingRight: 8,
});

const ItemRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 16,
  marginBottom: 16,
});

const ItemDetails = styled(Box)({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const ProductTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: "clamp(0.85rem, 1.2vw, 1rem)",
});

const QuantityRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

const Footer = styled(Box)({
  borderTop: "1px solid #eee",
  display: "flex",
  flexDirection: "column",
  padding: 16,
  gap: 24,
});

const TotalRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  marginTop: 8,
});

const TotalLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: "clamp(0.85rem, 1.2vw, 1rem)",
});

const TotalValue = styled(Typography)({
  fontWeight: 700,
  fontSize: "clamp(0.85rem, 1.2vw, 1rem)",
});

const CartPopover: React.FC<CartPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  items,
}) => {
  const dispatch = useDispatch();

  const itemsTotal = items.reduce(
    (acc, item) =>
      acc +
      Number(item.product.variants[item.variantIdx].price) * item.quantity,
    0,
  );

  const quantitiesNum = Array.from(
    { length: MAX_QUANITY_PER_PRODUCT },
    (_, i) => i + 1,
  );

  return (
    <StyledPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {items.length === 0 && <EmptyText>Your cart is empty</EmptyText>}

      {items.length > 0 && (
        <ItemsContainer>
          {items.map((item) => (
            <ItemRow key={item.product.id}>
              <Box
                component="img"
                src={item.product.image}
                alt={item.product.title}
                sx={{ width: 90, height: 100, borderRadius: 1 }}
              />
              <ItemDetails>
                <ProductTitle>{item.product.title}</ProductTitle>
                <Typography variant="body2" color="gray">
                  {item.collectionTitle}
                </Typography>
                <Typography variant="body2">
                  SIZE {item.product.variants[item.variantIdx].title}
                </Typography>
                <QuantityRow>
                  <Typography variant="body2" color="gray">
                    QUANTITY:
                  </Typography>
                  <CustomSelect
                    value={item.quantity}
                    width="clamp(50px, 25%, 120px)"
                    height="clamp(5px, 3vh, 20px)"
                    displayEmpty
                    onChange={(event: any) =>
                      dispatch(
                        setQuantity({
                          id: item.product.id,
                          variantIdx: item.variantIdx,
                          newQuantity: event.target.value,
                        }),
                      )
                    }
                  >
                    {quantitiesNum.map((quantity) => (
                      <MenuItem key={quantity} value={quantity}>
                        {quantity}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </QuantityRow>
              </ItemDetails>
              <IconButton
                size="small"
                onClick={() =>
                  dispatch(
                    removeItem({
                      id: item.product.id,
                      variantIdx: item.variantIdx,
                    }),
                  )
                }
                sx={{ color: "gray" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ItemRow>
          ))}
        </ItemsContainer>
      )}

      {items.length > 0 && (
        <Footer>
          <TotalRow>
            <TotalLabel>Total</TotalLabel>
            <TotalValue>{itemsTotal}₪</TotalValue>
          </TotalRow>
          <BuyButton items={items} />
        </Footer>
      )}
    </StyledPopover>
  );
};

export default CartPopover;
